import { useState, useEffect, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'
import { toast } from 'sonner'
import ConfidenceScoreBadge from './ConfidenceScoreBadge'
import { confirmDialog } from '@/lib/confirmDialog'

const LEVEL_LABELS = {
  boys_mite_8u: 'Boys Mite (8U)',
  boys_squirt_10u: 'Boys Squirt (10U)',
  boys_peewee_12u: 'Boys Peewee (12U)',
  boys_bantam_14u: 'Boys Bantam (14U)',
  girls_8u: 'Girls 8U',
  girls_10u: 'Girls 10U',
  girls_12u: 'Girls 12U',
  girls_15u: 'Girls 15U'
}

function getLevelLabel(value) {
  return LEVEL_LABELS[value] || value || '-'
}

export default function PendingPostingsTable() {
  const [postings, setPostings] = useState([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(new Set())
  const [teamFilter, setTeamFilter] = useState('all')
  const [selectedIds, setSelectedIds] = useState(new Set())

  useEffect(() => {
    fetchPendingPostings()
  }, [])

  async function fetchPendingPostings() {
    try {
      setLoading(true)
      
      const { data, error } = await supabase
        .from('pending_auto_postings')
        .select(`
          *,
          team:teams(
            name,
            association_name,
            team_level,
            level_detail
          )
        `)
        .eq('status', 'pending')
        .order('date', { ascending: true })
        .order('start_time', { ascending: true })

      if (error) throw error

      const sortedData = (data || []).sort((a, b) => {
        const dateCompare = (a.date || '').localeCompare(b.date || '')
        if (dateCompare !== 0) return dateCompare
        return (a.start_time || '').localeCompare(b.start_time || '')
      })

      setPostings(sortedData)
      setSelectedIds(new Set())
      setTeamFilter(prev => {
        if (prev === 'all') return prev
        return sortedData.some(p => p.team_id === prev) ? prev : 'all'
      })
    } catch (error) {
      console.error('Error fetching pending postings:', error)
      toast.error('Failed to load pending postings')
    } finally {
      setLoading(false)
    }
  }

  async function processPostings(ids, action, options = {}) {
    if (!ids || ids.length === 0) return

    setProcessing(prev => {
      const next = new Set(prev)
      ids.forEach(id => next.add(id))
      return next
    })

    try {
      const { data: { session } } = await supabase.auth.getSession()

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approve-auto-posting`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pending_posting_ids: ids,
            action,
            notes: options.notes || null
          })
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || `${action} failed`)
      }

      const summary = result.summary || { approved: 0, rejected: 0, failed: 0 }
      const failures = result.failures || []

      if (options.successMessage && summary.failed === 0 && failures.length === 0) {
        toast.success(options.successMessage)
      } else {
        const verb = action === 'approve' ? 'Approved' : 'Rejected'
        const successCount = action === 'approve' ? summary.approved : summary.rejected
        const failureCount = summary.failed || failures.length

        if (successCount > 0) {
          toast.success(`${verb} ${successCount} posting${successCount !== 1 ? 's' : ''}`)
        }

        if (failureCount > 0) {
          toast.warning(`${failureCount} posting${failureCount !== 1 ? 's' : ''} failed to ${action}`)
        }
      }

      fetchPendingPostings()
    } catch (error) {
      console.error(`Error processing postings (${action}):`, error)
      toast.error(`Failed to ${action}: ${error.message}`)
    } finally {
      setProcessing(prev => {
        const next = new Set(prev)
        ids.forEach(id => next.delete(id))
        return next
      })
    }
  }

  async function handleApprove(posting) {
    await processPostings([posting.id], 'approve', {
      successMessage: `Approved posting for ${posting.team.name}`
    })
  }

  async function handleReject(posting) {
    await processPostings([posting.id], 'reject', {
      successMessage: `Rejected posting for ${posting.team.name}`
    })
  }

  async function handleApproveSelected() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) {
      toast.info('Select at least one posting to approve')
      return
    }

    await processPostings(ids, 'approve')
  }

  async function handleRejectSelected() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) {
      toast.info('Select at least one posting to reject')
      return
    }

    await processPostings(ids, 'reject')
  }

  async function handleApproveHighConfidence(filteredHighConfidence) {
    if (filteredHighConfidence.length === 0) {
      toast.info('No high-confidence postings to approve')
      return
    }

    const confirmed = await confirmDialog(
      `Approve ${filteredHighConfidence.length} high-confidence posting${filteredHighConfidence.length !== 1 ? 's' : ''}?`,
      {
        confirmText: 'Approve',
        cancelText: 'Cancel',
        description: 'This will publish all visible high-confidence opportunities to the scrimmage bulletin.'
      }
    )

    if (!confirmed) return

    await processPostings(filteredHighConfidence.map(p => p.id), 'approve')
  }

  async function handleApproveVisible(filtered) {
    if (filtered.length === 0) {
      toast.info('No postings to approve')
      return
    }

    const confirmed = await confirmDialog(
      `Approve all ${filtered.length} visible posting${filtered.length !== 1 ? 's' : ''}?`,
      {
        confirmText: 'Approve All',
        cancelText: 'Cancel',
        description: 'All filtered opportunities will be published to the scrimmage bulletin.'
      }
    )

    if (!confirmed) return

    await processPostings(filtered.map(p => p.id), 'approve')
  }

  function formatDate(dateStr, timeStr) {
    if (!dateStr) return '-'
    const [year, month, day] = dateStr.split('-').map(Number)
    let hours = 12
    let minutes = 0
    if (timeStr) {
      const timeParts = timeStr.split(':').map(Number)
      if (!timeParts.some(Number.isNaN)) {
        hours = timeParts[0]
        minutes = timeParts[1]
      }
    }
    const date = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0))
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Chicago'
    }).format(date)
  }

  function formatTime(timeStr) {
    if (!timeStr) return '-'
    const [hours, minutes] = timeStr.split(':').map(Number)
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeStr
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const displayHour = hours % 12 === 0 ? 12 : hours % 12
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`
  }

  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev
      const allowedIds = new Set(postings.map(p => p.id))
      const next = new Set()
      prev.forEach(id => {
        if (allowedIds.has(id)) {
          next.add(id)
        }
      })
      if (next.size === prev.size) return prev
      return next
    })
  }, [postings])

  const filteredPostings = useMemo(() => {
    if (teamFilter === 'all') {
      return postings
    }
    return postings.filter(p => p.team_id === teamFilter)
  }, [postings, teamFilter])

  useEffect(() => {
    setSelectedIds(prev => {
      const allowed = new Set(filteredPostings.map(p => p.id))
      const next = new Set()
      prev.forEach(id => {
        if (allowed.has(id)) {
          next.add(id)
        }
      })
      if (next.size === prev.size) return prev
      return next
    })
  }, [filteredPostings])

  const teamOptions = useMemo(() => {
    const map = new Map()
    postings.forEach(posting => {
      if (!map.has(posting.team_id)) {
        map.set(posting.team_id, posting.team?.name || 'Unknown Team')
      }
    })
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [postings])

  const highConfidenceFiltered = useMemo(
    () => filteredPostings.filter(p => p.confidence_score >= 80),
    [filteredPostings]
  )

  const selectedCount = useMemo(() => {
    return filteredPostings.reduce((count, posting) => count + (selectedIds.has(posting.id) ? 1 : 0), 0)
  }, [filteredPostings, selectedIds])

  const allFilteredSelected = filteredPostings.length > 0 && selectedCount === filteredPostings.length

  function toggleSelect(postingId) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(postingId)) {
        next.delete(postingId)
      } else {
        next.add(postingId)
      }
      return next
    })
  }

  function toggleSelectAll() {
    if (filteredPostings.length === 0) return

    setSelectedIds(prev => {
      const next = new Set(prev)
      if (allFilteredSelected) {
        filteredPostings.forEach(posting => next.delete(posting.id))
      } else {
        filteredPostings.forEach(posting => next.add(posting.id))
      }
      return next
    })
  }

  if (loading) {
    return (
      <Card>
        <div className="p-8 text-center text-gray-600">Loading pending postings...</div>
      </Card>
    )
  }

  const highConfidenceCount = highConfidenceFiltered.length

  return (
    <Card>
      {/* Header */}
      <div className="p-6 border-b border-gray-200 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Pending Auto-Detected Postings
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Review and approve scrimmage opportunities detected from seed team calendars
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Teams</option>
              {teamOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
            {highConfidenceCount > 0 && (
              <Button
                onClick={() => handleApproveHighConfidence(highConfidenceFiltered)}
                className="bg-green-600 hover:bg-green-700"
              >
                Approve High-Confidence ({highConfidenceCount})
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => handleApproveVisible(filteredPostings)}
              disabled={filteredPostings.length === 0}
            >
              Approve All Visible
            </Button>
            <Button
              variant="ghost"
              onClick={handleApproveSelected}
              disabled={selectedCount === 0}
            >
              Approve Selected ({selectedCount})
            </Button>
            <Button
              variant="ghost"
              onClick={handleRejectSelected}
              disabled={selectedCount === 0}
              className="text-red-600 hover:text-red-700"
            >
              Reject Selected ({selectedCount})
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={filteredPostings.length > 0 && allFilteredSelected}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Team
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date & Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rink
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Confidence
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPostings.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  {postings.length === 0
                    ? 'No pending postings. Run calendar sync to detect opportunities.'
                    : 'No postings match your filter.'}
                </td>
              </tr>
            ) : (
              filteredPostings.map(posting => (
                <tr key={posting.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 align-middle">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(posting.id)}
                      onChange={() => toggleSelect(posting.id)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      disabled={processing.has(posting.id)}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{posting.team.name}</div>
                    <div className="text-sm text-gray-500">
                      {getLevelLabel(posting.team.team_level)}
                      {posting.team.level_detail ? ` • Tier ${posting.team.level_detail}` : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{formatDate(posting.date, posting.start_time)}</div>
                    <div className="text-sm text-gray-500">
                      {formatTime(posting.start_time)}
                      {posting.end_time && ` - ${formatTime(posting.end_time)}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {posting.duration_minutes} min
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {posting.rink_name || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <ConfidenceScoreBadge
                      score={posting.confidence_score}
                      reasons={posting.confidence_reasons}
                    />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleApprove(posting)}
                        disabled={processing.has(posting.id)}
                      >
                        {processing.has(posting.id) ? 'Processing...' : 'Approve'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReject(posting)}
                        disabled={processing.has(posting.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      {postings.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Viewing {filteredPostings.length} of {postings.length} pending posting{postings.length !== 1 ? 's' : ''}
            {' '}• {highConfidenceFiltered.length} high confidence (80+)
            {' '}• {filteredPostings.filter(p => p.confidence_score >= 60 && p.confidence_score < 80).length} medium (60-79)
            {selectedCount > 0 && ` • ${selectedCount} selected`}
          </div>
        </div>
      )}
    </Card>
  )
}

